import {
  Bot,
  BriefcaseBusiness,
  Cloud,
  DatabaseBackup,
  Globe2,
  KeyRound,
  Laptop,
  MessagesSquare,
  Network,
  PanelsTopLeft,
  ShieldCheck,
  Workflow,
} from 'lucide-react'

export type SolutionCategory =
  | 'foundations'
  | 'operations'
  | 'communication'
  | 'infrastructure'
  | 'continuity'

export type Solution = {
  id: string
  title: string
  summary: string
  detail: string
  categories: SolutionCategory[]
  icon: typeof Cloud
}

export const solutionCategories: Array<{
  id: SolutionCategory
  label: string
  description: string
}> = [
  {
    id: 'foundations',
    label: 'Foundations',
    description: 'The essential accounts, platforms, devices and controls a business needs.',
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Systems that organise information, remove repetition and support better work.',
  },
  {
    id: 'communication',
    label: 'Communication',
    description: 'Clear ways for teams, customers and partners to connect and collaborate.',
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    description: 'Cloud, on-premises and network foundations designed around the environment.',
  },
  {
    id: 'continuity',
    label: 'Continuity',
    description: 'Practical protection, recovery planning, ownership and documentation.',
  },
]

export const solutions: Solution[] = [
  {
    id: 'business-foundations',
    title: 'Business technology foundations',
    summary: 'Put the essential systems, ownership and working practices on a stable footing.',
    detail:
      'Domains, business-owned accounts, administration, onboarding and offboarding, password practices, documentation and a clear technology roadmap can all form part of the foundation.',
    categories: ['foundations', 'continuity'],
    icon: BriefcaseBusiness,
  },
  {
    id: 'cloud-collaboration',
    title: 'Cloud, email & collaboration',
    summary: 'Connect people and information through a well-structured working environment.',
    detail:
      'Email, calendars, shared files, collaboration, identity and access are designed together so that the environment remains understandable and manageable as the business changes.',
    categories: ['foundations', 'communication', 'infrastructure'],
    icon: Cloud,
  },
  {
    id: 'devices-access',
    title: 'Devices & access',
    summary: 'Give people appropriate access from secure, consistently configured devices.',
    detail:
      'Device setup, management, access controls, updates, encryption and recovery decisions are shaped around ownership, user needs and the agreed support model.',
    categories: ['foundations', 'continuity'],
    icon: Laptop,
  },
  {
    id: 'crm-systems',
    title: 'CRM & business systems',
    summary: 'Bring customer and operational information together around clearer processes.',
    detail:
      'Depending on the product and engagement, work may include requirements, selection, configuration, migration, integration, reporting, automation, documentation or handover.',
    categories: ['operations'],
    icon: PanelsTopLeft,
  },
  {
    id: 'digital-phones',
    title: 'Digital phones & communication',
    summary: 'Create a more coherent way to handle calls, messages and meetings.',
    detail:
      'Digital phone and communication solutions are considered alongside users, locations, numbers, devices, connectivity, continuity and the way customers contact the business.',
    categories: ['communication'],
    icon: MessagesSquare,
  },
  {
    id: 'networks',
    title: 'Networks & Wi-Fi',
    summary: 'Build reliable connectivity with clear separation, administration and ownership.',
    detail:
      'Network design can cover switching, routing, Wi-Fi, VLANs, guest networks, secure administration, remote access, virtualised environments, validation and documentation.',
    categories: ['infrastructure', 'continuity'],
    icon: Network,
  },
  {
    id: 'websites',
    title: 'Websites & e-commerce',
    summary: 'Create a professional digital presence connected to the wider business.',
    detail:
      'Website and e-commerce work can include structure, design, implementation, platform configuration, domains, forms, integrations, ownership and practical handover.',
    categories: ['operations', 'communication'],
    icon: Globe2,
  },
  {
    id: 'backup',
    title: 'Backup & continuity',
    summary: 'Protect important information and make recovery responsibilities explicit.',
    detail:
      'Backup and continuity work starts with what matters, where it lives, who owns it and what recovery should look like—then documents the agreed controls and next steps.',
    categories: ['continuity', 'infrastructure'],
    icon: DatabaseBackup,
  },
  {
    id: 'ai-automation',
    title: 'AI & automation',
    summary: 'Use appropriate automation to reduce repetitive work and improve information flow.',
    detail:
      'Opportunities are assessed against the actual process, data, risk and expected value before selecting AI, workflow automation, scripts or platform-native tools.',
    categories: ['operations'],
    icon: Bot,
  },
  {
    id: 'security-identity',
    title: 'Security & identity',
    summary: 'Strengthen access, administration and control without unnecessary complexity.',
    detail:
      'Identity, authentication, permissions, privileged access, configuration review and practical remediation can be addressed at a level proportionate to the environment.',
    categories: ['foundations', 'continuity', 'infrastructure'],
    icon: ShieldCheck,
  },
]

export const needPaths = [
  {
    id: 'start',
    label: 'Start or grow',
    title: 'Turn a home-grown setup into a business-ready IT foundation.',
    body: 'Bring business email, accounts, devices, files, backup and ownership together so your technology is ready for staff, customers and growth.',
    href: '/solutions#business-foundations',
    icon: BriefcaseBusiness,
  },
  {
    id: 'connect',
    label: 'Connect and collaborate',
    title: 'Make work easier to share and manage.',
    body: 'Improve email, files, collaboration, digital phones, websites and the ways people connect across locations and devices.',
    href: '/solutions#cloud-collaboration',
    icon: MessagesSquare,
  },
  {
    id: 'operations',
    label: 'Improve operations',
    title: 'Turn disconnected tools into useful systems.',
    body: 'Shape CRM, business applications, integrations, reporting, websites, AI and automation around the processes that actually need to work.',
    href: '/solutions#crm-systems',
    icon: Workflow,
  },
  {
    id: 'infrastructure',
    label: 'Strengthen infrastructure',
    title: 'Create a clearer technical foundation.',
    body: 'Address cloud, on-premises and hybrid infrastructure, virtualised environments, networks, Wi-Fi, remote access, security and continuity.',
    href: '/expertise',
    icon: Network,
  },
  {
    id: 'complexity',
    label: 'Resolve complexity',
    title: 'Bring senior judgement to difficult environments.',
    body: 'Work through complex Microsoft identity, directory, cloud, device, migration, remediation, automation and documentation challenges.',
    href: '/expertise#microsoft',
    icon: KeyRound,
  },
]

export const technologies = [
  'Microsoft 365',
  'Zoho',
  'Entra ID',
  'Active Directory',
  'Intune',
  'Windows Server',
  'Google Workspace',
  'UniFi',
  'PowerShell',
  'Wix',
]
